// Color utility functions for handling Tailwind color names
const tailwindColors = {
  // Blue shades - core colors
  "blue-500": "#3b82f6",
  "blue-600": "#2563eb",
  "blue-700": "#1d4ed8",

  // Emerald/Green shades - core colors
  "emerald-500": "#10b981",
  "emerald-600": "#059669",
  "emerald-700": "#047857",

  // Orange shades - core colors
  "orange-500": "#f97316",
  "orange-600": "#ea580c",
  "orange-700": "#c2410c",

  // Amber shades
  "amber-600": "#d97706",

  // Violet shades - core colors
  "violet-500": "#8b5cf6",
  "violet-600": "#7c3aed",
  "violet-700": "#6d28d9",

  // Red shades - popular colors
  "red-500": "#ef4444",
  "red-600": "#dc2626",
  "red-700": "#b91c1c",

  // Yellow shades - popular colors
  "yellow-500": "#eab308",
  "yellow-600": "#ca8a04",
  "yellow-700": "#a16207",

  // Pink shades - popular colors
  "pink-500": "#ec4899",
  "pink-600": "#db2777",
  "pink-700": "#be185d",

  // Indigo shades - popular colors
  "indigo-300": "#a5b4fc",
  "indigo-500": "#6366f1",
  "indigo-600": "#4f46e5",
  "indigo-700": "#4338ca",
  "indigo-800": "#3730a3",

  // Teal shades - popular colors
  "teal-300": "#5eead4",
  "teal-500": "#14b8a6",
  "teal-600": "#0d9488",
  "teal-700": "#0f766e",
  "teal-800": "#115e59",

  // Purple shades - popular colors
  "purple-300": "#d8b4fe",
  "purple-500": "#a855f7",
  "purple-600": "#9333ea",
  "purple-700": "#7c3aed",
  "purple-800": "#5b21b6",

  // Rose shades - trendy colors (primary only)
  "rose-500": "#f43f5e",
  "rose-600": "#e11d48",
  "rose-700": "#be123c",

  // Gray shades - essential neutrals
  "gray-100": "#f3f4f6",
  "gray-300": "#d1d5db",
  "gray-400": "#9ca3af",
  "gray-500": "#6b7280",
  "gray-600": "#4b5563",
  "gray-700": "#374151",
  "gray-800": "#1f2937",
  "gray-900": "#111827",

  // Slate shades - essential neutrals
  "slate-300": "#cbd5e1",
  "slate-400": "#94a3b8",
  "slate-500": "#64748b",
  "slate-600": "#475569",
  "slate-700": "#334155",
  "slate-800": "#1e293b",
  "slate-900": "#0f172a",

  // Stone shades - warm neutrals
  "stone-300": "#d6d3d1",
  "stone-400": "#a8a29e",
  "stone-500": "#78716c",
  "stone-600": "#57534e",
  "stone-700": "#44403c",

  // Zinc shades - cool neutrals
  "zinc-300": "#d4d4d8",
  "zinc-400": "#a1a1aa",
  "zinc-500": "#71717a",
  "zinc-600": "#52525b",
  "zinc-700": "#3f3f46",

  // Neutral shades - essential neutrals
  "neutral-300": "#d4d4d4",
  "neutral-400": "#a3a3a3",
  "neutral-500": "#737373",
  "neutral-600": "#525252",
  "neutral-700": "#404040",
  "neutral-800": "#262626",
  "neutral-900": "#171717",
};

/**
 * Convert Tailwind color name to CSS hex value
 * @param {string} colorName - Tailwind color name (e.g., 'blue-600')
 * @returns {string} CSS hex color value
 */
export const getColorValue = (colorName) => {
  return tailwindColors[colorName] || "#6b7280"; // fallback to gray-500
};

/**
 * Get a readable text color (black or white) based on background color
 * @param {string} backgroundColor - CSS hex color value
 * @returns {string} 'black' or 'white'
 */
export const getContrastColor = (backgroundColor) => {
  // Convert hex to RGB
  const hex = backgroundColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance using WCAG 2.1 formula
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  const rL =
    rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gL =
    gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bL =
    bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  const luminance = 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;

  // WCAG-derived threshold: ~0.179 is the luminance where contrast against
  // white equals contrast against black. Below it, white text wins; above, black.
  return luminance > 0.179 ? "black" : "white";
};

/**
 * Calculate the contrast ratio between two colors
 * @param {string} color1 - First color hex value
 * @param {string} color2 - Second color hex value
 * @returns {number} Contrast ratio
 */
export const getContrastRatio = (color1, color2) => {
  const getLuminance = (hex) => {
    const hexColor = hex.replace("#", "");
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);

    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;

    const rL =
      rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gL =
      gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bL =
      bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    return 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Get accessibility rating for a color combination
 * @param {string} backgroundColor - Background color hex
 * @param {string} textColor - Text color hex
 * @returns {object} Accessibility rating and details
 */
export const getAccessibilityRating = (backgroundColor, textColor) => {
  const ratio = getContrastRatio(backgroundColor, textColor);

  let rating = "Poor";
  let status = "❌";
  let description = "Poor contrast - may be difficult to read";

  if (ratio >= 7) {
    rating = "Excellent";
    status = "✅";
    description = "Excellent contrast - meets AAA standards";
  } else if (ratio >= 4.5) {
    rating = "Good";
    status = "✅";
    description = "Good contrast - meets AA standards";
  } else if (ratio >= 3) {
    rating = "Fair";
    status = "⚠️";
    description = "Fair contrast - may be acceptable for large text";
  }

  return {
    ratio: Math.round(ratio * 100) / 100,
    rating,
    status,
    description,
  };
};
